import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDistributorLedger, getDistributorBalance, getAgencies, exportDistributorExcel, exportDistributorPdf } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Buildings, ArrowUp, ArrowDown, Funnel, CurrencyInr, FileXls, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import OpeningBalanceImportDialog from '../components/OpeningBalanceImportDialog';
import SearchBar from '../components/SearchBar';

export default function DistributorLedgerPage() {
  const { user } = useAuth();
  const [ledger, setLedger] = useState([]);
  const [totals, setTotals] = useState(null);
  const [balances, setBalances] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [txnType, setTxnType] = useState('all');
  const [doneByRole, setDoneByRole] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('ledger');
  const [openingDlgOpen, setOpeningDlgOpen] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';
  const isDistributor = user?.role === 'distributor';
  const hasAccess = isAdmin || isDistributor;

  const agencyOptions = agencies.filter(a => (a.convenience_fee || 0) > 0);

  const agencySelectOptions = useMemo(() => [
    { value: 'all', label: 'All Agencies' },
    ...agencyOptions.map(a => ({ value: a.id, label: a.name }))
  ], [agencyOptions]);

  const TXN_TYPE_OPTIONS = useMemo(() => [
    { value: 'all', label: 'All Types' },
    { value: 'debit', label: 'Booking Debit' },
    { value: 'credit', label: 'Refund Credit' }
  ], []);

  const ROLE_OPTIONS = useMemo(() => [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Owner' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'staff', label: 'Staff' }
  ], []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const agencyId = selectedAgency === 'all' ? undefined : selectedAgency;
      const type = txnType === 'all' ? undefined : txnType;
      const role = doneByRole === 'all' ? undefined : doneByRole;
      const s = search.trim() || undefined;
      const [ledgerRes, balRes, agRes] = await Promise.all([
        getDistributorLedger(agencyId, startDate || undefined, endDate || undefined, type, role, s),
        getDistributorBalance(),
        isAdmin ? getAgencies() : Promise.resolve({ data: [] })
      ]);
      setLedger(ledgerRes.data.ledger);
      setTotals(ledgerRes.data.totals);
      setBalances(balRes.data);
      if (isAdmin) setAgencies(agRes.data);
    } catch (err) {
      toast.error('Data load nahi hua');
    } finally {
      setLoading(false);
    }
  }, [selectedAgency, startDate, endDate, txnType, doneByRole, search, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!hasAccess) return <div className="p-4 text-red-600">Access denied.</div>;

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const xhrDownload = (apiPath, filename) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${window.location.origin}${apiPath}`;
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.withCredentials = true;
      xhr.timeout = 30000;
      xhr.onload = () => {
        if (xhr.status === 200) {
          downloadFile(xhr.response, filename);
          resolve();
        } else reject(new Error(`Download failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Server se connect nahi ho paya'));
      xhr.ontimeout = () => reject(new Error('Request timeout'));
      xhr.send();
    });
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedAgency !== 'all') params.set('agency_id', selectedAgency);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (txnType !== 'all') params.set('txn_type', txnType);
      await xhrDownload(`/api/distributor-ledger/export-excel?${params}`, 'distributor_statement.xlsx');
      toast.success('Excel download ho gaya!');
    } catch (err) { toast.error('Export fail hua'); }
    finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedAgency !== 'all') params.set('agency_id', selectedAgency);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (txnType !== 'all') params.set('txn_type', txnType);
      await xhrDownload(`/api/distributor-ledger/export-pdf?${params}`, 'distributor_statement.pdf');
      toast.success('PDF download ho gaya!');
    } catch (err) { toast.error('Export fail hua'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="distributor-ledger-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Buildings size={28} weight="fill" className="text-[#002FA7]" /> Distributor Statement
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant={view === 'ledger' ? 'default' : 'outline'} onClick={() => setView('ledger')} size="sm" data-testid="view-ledger-btn">Ledger</Button>
          <Button variant={view === 'summary' ? 'default' : 'outline'} onClick={() => setView('summary')} size="sm" data-testid="view-summary-btn">Agency Summary</Button>
          <Button onClick={handleExportExcel} disabled={exporting || ledger.length === 0} variant="outline" size="sm" className="gap-1 text-green-700 border-green-300 hover:bg-green-50" data-testid="dist-export-excel">
            <FileXls size={16} weight="fill" /> Excel
          </Button>
          <Button onClick={handleExportPdf} disabled={exporting || ledger.length === 0} variant="outline" size="sm" className="gap-1 text-red-700 border-red-300 hover:bg-red-50" data-testid="dist-export-pdf">
            <FilePdf size={16} weight="fill" /> PDF
          </Button>
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <Button onClick={() => setOpeningDlgOpen(true)} variant="outline" size="sm" className="gap-1 text-violet-700 border-violet-300 hover:bg-violet-50" data-testid="dist-opening-balance-btn">
              📥 Opening Balance Import
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="dist-filters">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-600">
          <Funnel size={16} /> Filters
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Agency</Label>
            <SearchableSelect
              options={agencySelectOptions}
              value={selectedAgency}
              onValueChange={setSelectedAgency}
              placeholder="All"
              data-testid="dist-agency-filter"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="dist-start-date" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="dist-end-date" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Type</Label>
            <SearchableSelect
              options={TXN_TYPE_OPTIONS}
              value={txnType}
              onValueChange={setTxnType}
              placeholder="All"
              data-testid="dist-type-filter"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Done By</Label>
            <SearchableSelect
              options={ROLE_OPTIONS}
              value={doneByRole}
              onValueChange={setDoneByRole}
              placeholder="All"
              data-testid="dist-role-filter"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Search</Label>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search agency, txn, amount..."
              matchCount={ledger.length}
              noun="entries"
              testId="dist-search"
              inputClassName="pl-8 pr-7 h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards — Opening + Debit + Credit + Net + Closing (Rule #1 consistency) */}
      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="dist-summary">
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-3" data-testid="dist-opening-balance">
            <p className="text-xs text-gray-600 uppercase font-semibold">Opening Balance</p>
            <p className={`text-lg font-bold font-mono ${(totals.opening_balance || 0) >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
              {formatCurrency(totals.opening_balance || 0)}
            </p>
            <p className="text-[10px] text-gray-500">Pre-range</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-700 uppercase font-semibold">Total Debit</p>
            <p className="text-lg font-bold text-red-700 font-mono flex items-center gap-1">
              <ArrowDown size={14} /> {formatCurrency(totals.total_fees_collected)}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs text-green-700 uppercase font-semibold">Total Credit</p>
            <p className="text-lg font-bold text-green-700 font-mono flex items-center gap-1">
              <ArrowUp size={14} /> {formatCurrency(totals.total_refunds)}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700 uppercase font-semibold">Net Fees</p>
            <p className="text-lg font-bold text-[#002FA7] font-mono flex items-center gap-1">
              <CurrencyInr size={14} /> {formatCurrency(totals.net_fees)}
            </p>
          </div>
          <div className={`${(totals.closing_balance || 0) >= 0 ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-lg p-3`} data-testid="dist-closing-balance">
            <p className="text-xs uppercase font-semibold opacity-80">Closing Balance</p>
            <p className="text-xl font-bold font-mono">
              {formatCurrency(totals.closing_balance)}
            </p>
            <p className="text-[10px] opacity-70">{totals.total_entries} entries</p>
          </div>
        </div>
      )}

      {/* Agency Tabs — Rule #4: each agency as a separate category */}
      {agencyOptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg px-2 pt-2" data-testid="dist-agency-tabs">
          <div className="flex items-center gap-1 overflow-x-auto pb-0">
            <button
              type="button"
              onClick={() => setSelectedAgency('all')}
              data-testid="dist-agency-tab-all"
              className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition ${
                selectedAgency === 'all'
                  ? 'border-[#002FA7] text-[#002FA7] bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              All Agencies <span className="ml-1 text-[10px] font-normal opacity-70">({agencyOptions.length})</span>
            </button>
            {agencyOptions.map((a) => {
              const bal = balances.find((b) => b.agency_id === a.id);
              const isActive = selectedAgency === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedAgency(a.id)}
                  data-testid={`dist-agency-tab-${a.id}`}
                  className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition ${
                    isActive
                      ? 'border-[#002FA7] text-[#002FA7] bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  title={bal ? `Net: ${formatCurrency(bal.net_balance)}` : ''}
                >
                  {a.name}
                  {bal && (
                    <span className={`ml-1 text-[10px] font-normal ${bal.net_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ({formatCurrency(bal.net_balance)})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Ledger View */}
      {view === 'ledger' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="dist-ledger-table">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[140px]">Date &amp; Time</TableHead>
                  <TableHead>TXN ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead className="text-right">Fee/Cyl</TableHead>
                  <TableHead className="text-right text-red-700">Debit</TableHead>
                  <TableHead className="text-right text-green-700">Credit</TableHead>
                  <TableHead className="text-right font-bold">Balance After</TableHead>
                  <TableHead>Done By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-gray-400 py-8">
                      Koi entry nahi mili
                    </TableCell>
                  </TableRow>
                ) : (
                  ledger.map((e, i) => (
                    <TableRow key={`${e.timestamp}-${e.agency_id}-${i}`} className={e.type === 'credit' ? 'bg-green-50/30' : ''} data-testid={`dist-row-${i}`}>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap" title={e.timestamp}>
                        {e.timestamp ? new Date(e.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : e.date}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">{e.transaction_id || '-'}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${e.type === 'debit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {e.type === 'debit' ? 'Booking Debit' : 'Refund Credit'}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-sm">{e.agency_name}</TableCell>
                      <TableCell className="text-sm">{e.staff_name}</TableCell>
                      <TableCell className="text-center text-sm font-mono">{e.quantity}</TableCell>
                      <TableCell className="text-right text-sm font-mono">{formatCurrency(e.fee_per_cylinder)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {e.type === 'debit' ? <span className="text-red-600 font-semibold">{formatCurrency(e.amount)}</span> : <span className="text-gray-300">-</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {e.type === 'credit' ? <span className="text-green-600 font-semibold">{formatCurrency(e.amount)}</span> : <span className="text-gray-300">-</span>}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-sm font-bold ${e.balance >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                        {formatCurrency(e.balance)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {e.done_by}
                        {e.done_by_role && (
                          <span className={`ml-1 text-[10px] px-1 py-0.5 rounded ${e.done_by_role === 'admin' ? 'bg-blue-100 text-blue-700' : e.done_by_role === 'assistant' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                            {e.done_by_role === 'admin' ? 'Owner' : e.done_by_role === 'assistant' ? 'Asst' : 'Staff'}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Agency Summary View */}
      {view === 'summary' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="dist-summary-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Agency</TableHead>
                <TableHead className="text-right">Total Debit</TableHead>
                <TableHead className="text-right">Total Credit</TableHead>
                <TableHead className="text-right">Net Fee</TableHead>
                <TableHead className="text-center">Txns</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-400 py-8">
                    Koi data nahi mila
                  </TableCell>
                </TableRow>
              ) : (
                balances.map((b) => (
                  <TableRow key={b.agency_id} data-testid={`dist-balance-${b.agency_id}`}>
                    <TableCell className="font-medium">
                      {b.agency_name}
                      {b.rate > 0 && (
                        <div className="text-[10px] text-emerald-700 font-bold mt-0.5">💰 ₹{b.rate}/cyl</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 font-semibold">{formatCurrency(b.total_debit)}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">{formatCurrency(b.total_credit)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-[#002FA7]">{formatCurrency(b.net_balance)}</TableCell>
                    <TableCell className="text-center">{b.transaction_count}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <OpeningBalanceImportDialog
        open={openingDlgOpen}
        onClose={() => setOpeningDlgOpen(false)}
        onImported={() => { fetchData(); }}
      />
    </div>
  );
}
